/**
 * Notification policy — quiet hours, redaction, and delivery decisions.
 *
 * Local-first: in-app delivery works offline; network channels (Telegram, push,
 * voice) only deliver when online. Quiet hours hold non-urgent nudges until the
 * window ends instead of waking the owner. Redaction is on by default for
 * sensitive reminders and only relaxed on channels that are allowed full
 * content. All decisions are PURE; the data layer performs the side effects and
 * records a delivery log.
 */

import { type Reminder, isReminderPending } from './reminder.js';

export type ChannelId = 'in_app' | 'telegram' | 'whatsapp' | 'web_push' | 'voice';

export interface ChannelCaps {
  /** Network channels cannot deliver while offline. */
  readonly requiresNetwork: boolean;
  /** Whether the channel may show full (unredacted) content. */
  readonly allowsFullContent: boolean;
}

export interface QuietHours {
  readonly enabled: boolean;
  /** UTC hour [0,23] the quiet window starts. */
  readonly startHour: number;
  /** UTC hour [0,23] the quiet window ends (exclusive). May wrap past midnight. */
  readonly endHour: number;
}

export const DEFAULT_QUIET_HOURS: QuietHours = { enabled: true, startHour: 22, endHour: 7 };

/** Is the instant inside the quiet-hours window? Handles windows that wrap midnight. Pure. */
export function isWithinQuietHours(iso: string, q: QuietHours): boolean {
  if (!q.enabled || q.startHour === q.endHour) return false;
  const h = new Date(iso).getUTCHours();
  return q.startHour < q.endHour
    ? h >= q.startHour && h < q.endHour
    : h >= q.startHour || h < q.endHour;
}

/** The next instant at which quiet hours end, at or after `iso`. Pure. */
export function quietHoursEndIso(iso: string, q: QuietHours): string {
  const d = new Date(iso);
  d.setUTCHours(q.endHour, 0, 0, 0);
  if (d.getTime() <= Date.parse(iso)) d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString();
}

export type DeliveryDecision =
  | { readonly kind: 'deliver'; readonly redacted: boolean }
  | { readonly kind: 'hold_quiet_hours'; readonly untilIso: string }
  | { readonly kind: 'not_due' }
  | { readonly kind: 'offline' }
  | { readonly kind: 'skip'; readonly reason: string };

export interface DeliveryContext {
  readonly nowIso: string;
  readonly quietHours: QuietHours;
  readonly online: boolean;
}

/** Decide what to do with a reminder on a channel right now. Pure, side-effect free. */
export function decideDelivery(
  reminder: Pick<Reminder, 'fireAt' | 'state' | 'redactByDefault'>,
  ctx: DeliveryContext,
  channel: ChannelCaps,
): DeliveryDecision {
  if (!isReminderPending(reminder.state)) {
    return { kind: 'skip', reason: `reminder is ${reminder.state}` };
  }
  if (Date.parse(reminder.fireAt) > Date.parse(ctx.nowIso)) {
    return { kind: 'not_due' };
  }
  if (channel.requiresNetwork && !ctx.online) {
    return { kind: 'offline' };
  }
  if (isWithinQuietHours(ctx.nowIso, ctx.quietHours)) {
    return { kind: 'hold_quiet_hours', untilIso: quietHoursEndIso(ctx.nowIso, ctx.quietHours) };
  }
  return { kind: 'deliver', redacted: reminder.redactByDefault && !channel.allowsFullContent };
}

export type DeliveryOutcome = 'delivered' | 'held_quiet_hours' | 'offline' | 'skipped' | 'failed';

/** One persisted delivery-log entry — the audit of what was (or wasn't) sent. */
export interface DeliveryRecord {
  readonly id: string;
  readonly reminderId: string;
  readonly channel: ChannelId;
  readonly outcome: DeliveryOutcome;
  readonly redacted: boolean;
  readonly at: string;
  readonly detail: string;
}
